<script setup>
import { useApi } from '@/composables/useApi'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    requiresAuth: true,
    roles: ['surgeon_secretary'],
  },
})

const surgeons = ref([])
const isLoading = ref(false)
const loadError = ref('')
const router = useRouter()

const headers = [
  { title: 'Surgeon', key: 'name', sortable: true },
  { title: 'GMC number', key: 'gmc_number', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Completeness', key: 'completeness', sortable: false },
  { title: 'Profile', key: 'actions', sortable: false },
]

const resolveName = surgeon => (
  surgeon?.full_name
  ?? surgeon?.name
  ?? [surgeon?.first_name, surgeon?.last_name].filter(Boolean).join(' ')
  ?? 'Unnamed contact'
)

const resolveGmc = surgeon => surgeon?.gmc_number ?? surgeon?.gmc_id ?? surgeon?.gmcId ?? null

const resolveStatus = surgeon => (surgeon?.is_active ? 'Active' : 'Inactive')

const resolveStatusBadge = surgeon => {
  const isActive = Boolean(surgeon?.is_active)

  return {
    color: isActive ? 'success' : 'error',
    variant: 'tonal',
    className: isActive ? 'badge-status--active' : 'badge-status--inactive',
  }
}

const resolveRowItem = item => item?.raw ?? item
const resolveSurgeonId = item => resolveRowItem(item)?.id ?? null

const isDraft = item => {
  const s = resolveRowItem(item)

  return (s?.hospitals?.length ?? 0) === 0 || (s?.specialist_areas?.length ?? 0) === 0
}

const tableItems = computed(() => surgeons.value ?? [])

const openSurgeonProfile = item => {
  const surgeonId = resolveSurgeonId(item)

  if (!surgeonId)
    return

  router.push({
    name: 'surgeon-id-tab',
    params: { id: String(surgeonId), tab: 'profile' },
  })
}

const fetchSurgeons = async () => {
  isLoading.value = true
  loadError.value = ''

  try {
    const { data, error } = await useApi('/api/secretary/surgeons?active=1')
    if (error.value)
      throw error.value

    const payload = data.value?.data ?? data.value ?? []

    surgeons.value = Array.isArray(payload) ? payload : (payload?.surgeons ?? [])
  } catch (err) {
    loadError.value = 'Unable to load associated surgeons.'
    console.error('Failed to load surgeons for secretary', err)
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchSurgeons)
</script>

<template>
  <VContainer class="py-6">
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <div>
          <div class="text-h4">
            Associated surgeons
          </div>
          <div class="text-body-2 text-medium-emphasis mt-1">
            View the surgeons linked to your account and open their profiles.
          </div>
        </div>
        <VBtn
          variant="text"
          prepend-icon="ri-refresh-line"
          @click="fetchSurgeons"
        >
          Refresh
        </VBtn>
        <VBtn
          color="primary"
          prepend-icon="ri-user-add-line"
          @click="router.push({ name: 'surgeon-id-tab', params: { id: 'new', tab: 'profile' } })"
        >
          New Surgeon
        </VBtn>
      </VCardTitle>
      <VDivider />
      <VCardText>
        <VAlert
          v-if="loadError"
          type="error"
          class="mb-4"
        >
          {{ loadError }}
        </VAlert>

        <VDataTable
          :headers="headers"
          :items="tableItems"
          :loading="isLoading"
          item-key="id"
          class="text-no-wrap"
        >
          <template #item.name="{ item }">
            {{ resolveName(resolveRowItem(item)) }}
          </template>
          <template #item.gmc_number="{ item }">
            {{ resolveGmc(resolveRowItem(item)) || '—' }}
          </template>
          <template #item.status="{ item }">
            <VChip
              size="small"
              :color="resolveStatusBadge(resolveRowItem(item)).color"
              :variant="resolveStatusBadge(resolveRowItem(item)).variant"
              class="badge-status"
              :class="[resolveStatusBadge(resolveRowItem(item)).className]"
            >
              {{ resolveStatus(resolveRowItem(item)) }}
            </VChip>
          </template>
          <template #item.completeness="{ item }">
            <VChip
              v-if="isDraft(item)"
              size="small"
              color="warning"
              variant="tonal"
              prepend-icon="ri-error-warning-line"
            >
              Draft
            </VChip>
            <VIcon
              v-else
              color="success"
              icon="ri-checkbox-circle-line"
            />
          </template>
          <template #item.actions="{ item }">
            <VBtn
              size="small"
              :disabled="!resolveSurgeonId(item)"
              @click.stop="openSurgeonProfile(item)"
            >
              View profile
            </VBtn>
          </template>
          <template #no-data>
            <div class="text-body-2 text-medium-emphasis">
              No surgeons linked to your account yet.
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>
  </VContainer>
</template>
